import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';

const AiSentimentContext = createContext({});

export function AiSentimentProvider({
  children,
  serviceJobId = null,
  jobStatus = 'INCOMPLETE',
}) {
  const userAccess = useUserAccess();
  const [loadingAiAnalysis, setLoadingAiAnalysis] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const display = userAccess.isSA() || userAccess.isAdmin();

  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const fetchSentiment = async jobId => {
    const url = `${getApiUrl()}/sxcl/v1/insights/communication/${jobId}/analysis`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const initialize = async () => {
    setLoadingAiAnalysis(true);
    try {
      const result = await fetchSentiment(serviceJobId);
      setAiAnalysis({
        ai: result,
      });
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err.message }]} />,
      });
    }
    setLoadingAiAnalysis(false);
  };

  useEffect(() => {
    if (display && serviceJobId) {
      initialize();
    } else {
      setLoadingAiAnalysis(false);
    }
  }, [serviceJobId, jobStatus, display]);

  const value = useMemo(
    () => ({ loadingAiAnalysis, aiAnalysis }),
    [loadingAiAnalysis, aiAnalysis],
  );

  return (
    <AiSentimentContext.Provider value={value}>
      {children}
    </AiSentimentContext.Provider>
  );
}

export const useAiSentiment = () => {
  return useContext(AiSentimentContext);
};
